import { FC, useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Theme } from '../../../../../../../../../shared/styles/theme/theme.ts';
import { useTranslation } from 'react-i18next';
import { IThirdStepData } from '../content.popup.tsx';
import { Table } from '../../../../../../../../../shared/components/table/table.component.tsx';
import {
  Column,
  Footer,
} from '../../../../../../../../../shared/components/table/components/common.components.tsx';
import { C_Import_Skip_Reason } from '../../../../../../../../../graphql/generatedModel.ts';

/**
 * The field a skipped article was rejected on, under the label the operator knows
 * it by. A field without a label here still shows up under its API name rather
 * than disappearing - better a technical word than a silent omission.
 */
const FIELD_LABELS: Record<string, string> = {
  articleNo: 'article.article_no',
  description: 'common.description',
  eanCode: 'article.ean_code',
  sellingWeight: 'article.selling_weight_piece',
};

export const ThirdStep: FC<IThirdStep> = ({ thirdStepData: { reimportResult } }) => {
  const { t } = useTranslation();
  const { recordsUpdated, recordsInserted, recordsSkipped, skippedRecords } = reimportResult;
  const createdText =
    typeof recordsInserted === 'number'
      ? t('settings.article_import_created', { count: recordsInserted })
      : '';
  const updatedText =
    typeof recordsUpdated === 'number'
      ? t('settings.article_import_updated', { count: recordsUpdated })
      : '';
  const skippedText =
    typeof recordsSkipped === 'number' && recordsSkipped > 0
      ? t('settings.article_import_skipped', { count: recordsSkipped })
      : '';
  const summary = [createdText, updatedText, skippedText].filter(Boolean).join(', ');
  const skippedRows = useMemo(
    () => (skippedRecords || []).map((record, index) => ({ ...record, id: index })),
    [skippedRecords],
  );

  return (
    <Stack
      sx={{
        alignItems: 'center',
        height: 'calc(100vh - 177px)',
        justifyContent: 'center',
        overflowY: 'scroll',
      }}
    >
      <Stack
        sx={{
          backgroundColor: Theme.palette.primary.main,
          borderRadius: '50%',
          width: 63,
          height: 63,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CheckIcon sx={{ fill: Theme.palette.background.default, fontSize: 55, fontWeight: 600 }} />
      </Stack>
      <Typography sx={{ fontWeight: 600, fontSize: 24, mb: 1, mt: 2 }}>
        {t('common.finished')}
      </Typography>
      <Typography
        sx={{
          fontSize: 16,
          color: Theme.palette.text.secondary,
          fontWeight: 500,
          mb: 1,
          mt: '10px',
        }}
      >
        {t('settings.article_import_title')}
      </Typography>
      <Typography
        sx={{
          fontSize: 16,
          color: Theme.palette.text.secondary,
          fontWeight: 500,
          mb: 1,
          mt: '10px',
          textDecoration: 'underline',
        }}
      >
        {summary ? `${summary}.` : ''}
      </Typography>
      {skippedRows.length > 0 && (
        <Stack sx={{ width: '100%', mt: 2 }}>
          <Typography
            sx={{
              fontSize: 16,
              color: Theme.palette.text.secondary,
              fontWeight: 500,
              mb: 1,
              textAlign: 'center',
            }}
          >
            {t('settings.articles_were_not_imported')}
          </Typography>
          <Table data={skippedRows as []} heightOffset={420}>
            <Column
              field='recordNo'
              headerName={t('settings.ca_number')}
              width={140}
              sortable={false}
            />
            <Column
              field='description'
              headerName={t('settings.ca_description')}
              width={180}
              sortable={false}
            />
            <Column
              field='reason'
              headerName={t('settings.skip_reason')}
              width={240}
              flex={1}
              sortable={false}
              valueGetter={(value: C_Import_Skip_Reason, row: (typeof skippedRows)[number]) => {
                const reason = t(`enums.${value}`);
                const label = row.field && FIELD_LABELS[row.field];
                // Which field, in the operator's words: "Value too long" on an article
                // whose number is fine means nothing without it.
                return label ? `${reason}: ${t(label)}` : reason;
              }}
            />
            <Footer
              numberRows={skippedRows.length}
              titleRows={t('article.articles', { count: skippedRows.length })}
            />
          </Table>
        </Stack>
      )}
    </Stack>
  );
};

interface IThirdStep {
  thirdStepData: IThirdStepData;
}
